'use client'

import { CloseIcon } from '../../../icons'
import React, { ReactElement, ReactNode, cloneElement, MutableRefObject, useRef } from 'react'
import { NotificationProps, NotificationVariant } from '../../../../types/Notification'
import { Button, ButtonProps, ButtonVariants, Text } from '../../../atoms'
import { getNotificationIcon } from '../helpers/getNotificationIcon'
import { renderMessage } from '../helpers/renderMessage'

export interface InlineNotificationProps extends NotificationProps {
    action?: ReactElement<ButtonProps>
    dismissText?: string
    isVertical?: boolean
}

const closeNotification = (ref: MutableRefObject<null>) => {
    if (ref.current === null) {
        console.warn('ref === null')
        return null
    }
    return (ref.current as HTMLDialogElement).close()
}
export const InlineNotification = ({
    id,
    title,
    message,
    dismissText = 'Dismiss',
    variant = NotificationVariant.INFO,
    dismissable = true,
    hideIcon = false,
    customIcon,
    action,
    isVertical = false,
    onRemove,
    ...props
}: InlineNotificationProps) => {
    if (onRemove !== undefined) console.warn('onRemove will not be used to dismiss InlineNotification.')
    const ref = useRef(null)

    const variantClasses = {
        [NotificationVariant.SHADE]: 'bg-shade-opacity-100',
        [NotificationVariant.INFO]: 'bg-info-100',
        [NotificationVariant.SUCCESS]: 'bg-success-100',
        [NotificationVariant.WARNING]: 'bg-warning-100',
        [NotificationVariant.ERROR]: 'bg-error-100',
    }

    const variantIconClasses = {
        [NotificationVariant.SHADE]: 'text-shade-700',
        [NotificationVariant.INFO]: 'text-info-700',
        [NotificationVariant.SUCCESS]: 'text-success-700',
        [NotificationVariant.WARNING]: 'text-warning-700',
        [NotificationVariant.ERROR]: 'text-error-700',
    }

    const iconElement: ReactNode = getNotificationIcon(variant, customIcon)
    const sharedDismissBtnProps: ButtonProps = {
        variant: ButtonVariants.QUIET,
        onClick: () => closeNotification(ref),
        'aria-label': dismissText,
    }

    return (
        <dialog
            {...props}
            className={`rounded-default inline-notification ${variantClasses[variant]} ${
                props?.className || ''
            } inline-notification--${isVertical ? 'vertical' : 'horizontal'}`}
            open
            role="alert"
            ref={ref}
        >
            <div className="inline-notification__content-wrapper p-3 flex min-w-0 my-auto rounded-default">
                {!hideIcon && iconElement && (
                    <div className={`shrink-0 my-auto ${variantIconClasses[variant]}`} aria-hidden="true">
                        {iconElement}
                    </div>
                )}
                <div className="inline-notification__text">
                    {title && (
                        <Text className="text-lg text-text-primary inline mr-2 font-medium" tag="h1">
                            {title}
                        </Text>
                    )}
                    {renderMessage({ message, tag: 'p', classes: 'text-text-default' })}
                </div>
            </div>

            {!isVertical && (
                <>
                    {!!action && (
                        <div className="inline-flex border-r border-r-shade-opacity-200 shrink-0">
                            {cloneElement(action, { variant: ButtonVariants.QUIET })}
                        </div>
                    )}
                    {dismissable && (
                        <div className="shrink-0 inline-flex">
                            <Button {...sharedDismissBtnProps} icon={<CloseIcon />} />
                        </div>
                    )}
                </>
            )}

            {isVertical && (!!action || dismissable) && (
                <div className="flex w-full justify-evenly border-border-default border-t">
                    {!!action && cloneElement(action, { className: 'w-full', variant: ButtonVariants.QUIET })}
                    {action && dismissable && <span className="w-[1px] border-border-default border-r" />}
                    {dismissable && (
                        <Button {...sharedDismissBtnProps} className="w-full">
                            {dismissText}
                        </Button>
                    )}
                </div>
            )}
        </dialog>
    )
}
